
import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import LandingPage from './pages/LandingPage.tsx';
import Dashboard from './pages/DashboardPage.tsx';
import PricingPage from './pages/PricingPage.tsx';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage.tsx';
import TermsPage from './pages/TermsPage.tsx';
import { api } from './services/api';
// @ts-ignore
import { Analytics } from "@vercel/analytics/dist/react/index.js";
// @ts-ignore
import { SpeedInsights } from "@vercel/speed-insights/dist/react/index.js";

const App: React.FC = () => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        checkAuth();
    }, []);

    // Scroll to top on every route change
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    useEffect(() => {
        const updateTheme = () => {
            const savedTheme = localStorage.getItem('setting_theme') || 'System';
            let isDark = false;
            if (savedTheme === 'Dark') {
                isDark = true;
            } else if (savedTheme === 'System' || !savedTheme) {
                isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            }
            if (isDark) {
                document.documentElement.classList.add('dark');
            } else {
                document.documentElement.classList.remove('dark');
            }
        };

        updateTheme();

        window.addEventListener('neura-theme-change', updateTheme);
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        mediaQuery.addEventListener('change', updateTheme);

        return () => {
            window.removeEventListener('neura-theme-change', updateTheme);
            mediaQuery.removeEventListener('change', updateTheme);
        };
    }, []);

    const checkAuth = async () => {
        try {
            const session = await api.getAuthSession();
            setIsAuthenticated(!!session);
        } catch (e) {
            console.error("Auth check failed", e);
            setIsAuthenticated(false);
        }
    };

    const handleEnterApp = async () => {
        // Trigger Google OAuth Flow
        const result = await api.signInWithGoogle();

        if (result && result.isMock) {
            setIsAuthenticated(true);
            window.scrollTo(0, 0);
            navigate('/');
        }
    };

    // Loading State
    if (isAuthenticated === null) {
        return (
            <div className="w-full h-screen flex items-center justify-center bg-white dark:bg-[#050505]">
                <div className="flex flex-col items-center gap-4 animate-pulse">
                    <div className="w-12 h-12 bg-gray-200 dark:bg-gray-800 rounded-xl"></div>
                    <div className="text-gray-400 dark:text-gray-500 text-sm font-medium">Initializing Neura...</div>
                </div>
            </div>
        );
    }

    return (
        <>
            <Routes>
                <Route path="/pricing" element={
                    <PricingPage onClose={() => navigate('/')} />
                } />
                <Route path="/privacy-policy" element={
                    <PrivacyPolicyPage onClose={() => navigate('/')} />
                } />
                <Route path="/terms" element={
                    <TermsPage onClose={() => navigate('/')} />
                } />
                <Route path="/" element={
                    isAuthenticated ? <Dashboard /> : <LandingPage onEnterApp={handleEnterApp} />
                } />
                <Route path="*" element={
                    isAuthenticated ? <Dashboard /> : <LandingPage onEnterApp={handleEnterApp} />
                } />
            </Routes>
            <Analytics />
            <SpeedInsights />
        </>
    );
};

export default App;
